import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Mail, Search, Trash2, CheckCircle2, Circle, Building2, Calendar, RefreshCcw } from "lucide-react";
import { useAdminAuth, formatApiErrorDetail } from "../../lib/adminAuth";

const POLL_INTERVAL = 15000; // 15 saniye

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // İlk ton
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.frequency.value = 880;
    osc1.type = "sine";
    gain1.gain.setValueAtTime(0.3, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.4);
    // İkinci ton (biraz sonra)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.frequency.value = 1100;
    osc2.type = "sine";
    gain2.gain.setValueAtTime(0.2, ctx.currentTime + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.55);
    osc2.start(ctx.currentTime + 0.15);
    osc2.stop(ctx.currentTime + 0.55);
  } catch (_) {
    // Ses desteklenmiyorsa sessizce geç
  }
}

export default function AdminMessages() {
  const { authedClient } = useAdminAuth();
  const [data, setData] = useState({ items: [], unread: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [selected, setSelected] = useState(null);
  const knownTotalRef = useRef(null); // polling için bilinen toplam mesaj sayısı

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data: payload } = await authedClient.get("/admin/messages", {
        params: { q: q || undefined, only_unread: onlyUnread },
      });
      setData(payload);

      // İlk yüklemede referans sayıyı kaydet, sonraki polling'de karşılaştır
      if (knownTotalRef.current === null) {
        knownTotalRef.current = payload.total;
      } else if (payload.total > knownTotalRef.current) {
        const newCount = payload.total - knownTotalRef.current;
        knownTotalRef.current = payload.total;
        playNotificationSound();
        toast.info(`${newCount} yeni mesaj geldi`, { duration: 4000 });
      } else {
        knownTotalRef.current = payload.total;
      }
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yüklenemedi");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authedClient, q, onlyUnread]);

  // İlk yükleme + filtre değişince
  useEffect(() => {
    const id = setTimeout(() => load(), 200);
    return () => clearTimeout(id);
  }, [load]);

  // Polling — 15 saniyede bir sessizce kontrol et
  useEffect(() => {
    const id = setInterval(() => load({ silent: true }), POLL_INTERVAL);
    return () => clearInterval(id);
  }, [load]);

  const markRead = async (id, read) => {
    try {
      await authedClient.patch(`/admin/messages/${id}/read`, null, { params: { read } });
      setData((p) => ({
        ...p,
        items: p.items.map((m) => (m.id === id ? { ...m, read } : m)),
        unread: p.unread + (read ? -1 : 1),
      }));
      if (selected?.id === id) setSelected((s) => (s ? { ...s, read } : s));
    } catch (e) {
      toast.error("Güncellenemedi");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu mesajı silmek istediğinize emin misiniz?")) return;
    try {
      await authedClient.delete(`/admin/messages/${id}`);
      toast.success("Mesaj silindi");
      setSelected(null);
      load();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  return (
    <div data-testid="admin-messages-page">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-7">
        <div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Mesajlar</h1>
          <p className="mt-1 text-sm text-ink/60">
            Toplam <strong>{data.total}</strong> · Okunmamış{" "}
            <strong className="text-terracotta-400">{data.unread}</strong>
          </p>
        </div>
        <button
          onClick={() => load()}
          data-testid="messages-refresh"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink/60 hover:text-ink"
        >
          <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
          Yenile
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" strokeWidth={1.5} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ara: isim, e-posta, mesaj…"
            data-testid="messages-search"
            className="admin-input pl-9"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-ink/70">
          <input
            type="checkbox"
            checked={onlyUnread}
            onChange={(e) => setOnlyUnread(e.target.checked)}
            data-testid="messages-unread-toggle"
            className="w-4 h-4 accent-sage-500"
          />
          Sadece okunmamış
        </label>
      </div>

      {loading && data.items.length === 0 ? (
        <div className="text-ink/55 text-sm py-12 text-center">Yükleniyor…</div>
      ) : data.items.length === 0 ? (
        <div className="surface-card p-10 text-center text-ink/60">
          <Mail className="w-8 h-8 mx-auto mb-3 text-ink/30" strokeWidth={1.4} />
          Henüz mesaj yok.
        </div>
      ) : (
        <ul className="space-y-2.5">
          {data.items.map((m) => (
            <li key={m.id}>
              <button
                onClick={() => {
                  setSelected(m);
                  if (!m.read) markRead(m.id, true);
                }}
                data-testid={`message-row-${m.id}`}
                className={`w-full text-left surface-card px-5 py-4 hover:shadow-soft transition-all ${
                  !m.read ? "border-l-4 border-l-terracotta-400" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    {m.read ? (
                      <CheckCircle2 className="w-4 h-4 text-sage-400 shrink-0" strokeWidth={1.5} />
                    ) : (
                      <Circle className="w-4 h-4 text-terracotta-400 shrink-0 fill-terracotta-400" strokeWidth={1.5} />
                    )}
                    <span className="font-medium text-ink truncate">{m.name}</span>
                    <span className="text-ink/45 text-sm truncate">· {m.email}</span>
                  </div>
                  <span className="text-xs text-ink/45 shrink-0 flex items-center gap-1">
                    <Calendar className="w-3 h-3" strokeWidth={1.5} />
                    {formatDate(m.created_at)}
                  </span>
                </div>
                {m.subject && <p className="text-sm font-medium text-ink/80 mb-1 truncate">{m.subject}</p>}
                {m.organization && (
                  <p className="text-xs text-ink/50 flex items-center gap-1 mb-1">
                    <Building2 className="w-3 h-3" strokeWidth={1.5} />
                    {m.organization}
                  </p>
                )}
                <p className="text-sm font-light text-ink/65 line-clamp-2">{m.message}</p>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Detail modal */}
      {selected && (
        <div
          data-testid="message-detail-modal"
          className="fixed inset-0 z-50 bg-ink/30 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSelected(null)}
        >
          <div
            className="surface-card w-full max-w-2xl p-7 md:p-9 max-h-[85vh] overflow-y-auto animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h3 className="font-serif text-3xl tracking-tight">{selected.name}</h3>
                <a href={`mailto:${selected.email}`} className="text-sm text-terracotta-400 hover:underline">
                  {selected.email}
                </a>
              </div>
              <button onClick={() => setSelected(null)} className="text-ink/40 hover:text-ink text-xl leading-none">×</button>
            </div>

            <dl className="grid grid-cols-2 gap-4 mb-5 text-sm">
              {selected.organization && (
                <div><dt className="admin-label">Kurum</dt><dd>{selected.organization}</dd></div>
              )}
              {selected.subject && (
                <div><dt className="admin-label">Konu</dt><dd>{selected.subject}</dd></div>
              )}
              <div><dt className="admin-label">Tarih</dt><dd>{formatDate(selected.created_at, true)}</dd></div>
              <div><dt className="admin-label">Durum</dt><dd>{selected.read ? "Okundu" : "Okunmadı"}</dd></div>
            </dl>

            <div className="bg-cream-200/40 border border-cream-300/60 rounded-xl p-5 mb-6">
              <p className="text-base text-ink/85 font-light whitespace-pre-wrap leading-relaxed">{selected.message}</p>
            </div>

            <div className="flex flex-wrap gap-2 justify-between">
              <div className="flex flex-wrap gap-2">
                <a
                  href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "İletişim")}`}
                  data-testid="message-reply-btn"
                  className="btn-accent text-xs"
                >
                  Yanıtla
                </a>
                <button
                  onClick={() => markRead(selected.id, !selected.read)}
                  data-testid="message-toggle-read"
                  className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2 text-xs hover:bg-cream-200"
                >
                  {selected.read ? "Okunmadı olarak işaretle" : "Okundu olarak işaretle"}
                </button>
              </div>
              <button
                onClick={() => remove(selected.id)}
                data-testid="message-delete"
                className="inline-flex items-center gap-2 rounded-full border border-terracotta-200 text-terracotta-500 px-5 py-2 text-xs hover:bg-terracotta-50"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(s, withTime = false) {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const date = d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
  if (!withTime) return date;
  return `${date} · ${d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}`;
}
